import { API_ROUTES, useApiService } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useCallback, useEffect, useState } from 'react';
import type { OfferBundleDetails } from '../types';

type UseOfferDataProps = {
  code?: string;
};

type UseOfferDataReturn = {
  data: OfferBundleDetails | null;
  loading: boolean;
  error: string | null;
};

function usePublicOfferBundleData({ code }: UseOfferDataProps): UseOfferDataReturn {
  const debug = createDebug({ isEnabled: false });
  const [data, setData] = useState<OfferBundleDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiService = useApiService();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let apiUrl = API_ROUTES.OFFER_BUNDLES.PUBLIC;
      apiUrl = apiUrl.replace(':offerbundleCode', code || '');

      const response = await apiService.get<OfferBundleDetails>(apiUrl);

      if (response.data) {
        debug.log('API Response:', response.data);
        setData(response.data);
      } else {
        setError('No data available');
      }
    } catch (err) {
      setError('Failed to fetch offer data');
      debug.error('Error fetching offer data:', err);
    } finally {
      setLoading(false);
    }

    // Note: 'debug' is intentionally excluded from dependencies to prevent infinite re-renders.
    // The debug object is recreated on every render, so including it would cause this callback
    // to be recreated unnecessarily. Since debug is only used for logging and doesn't affect
    // the callback's behavior, it's safe to exclude it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService, code]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error };
}

export { usePublicOfferBundleData };
